'use client';
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, TrendingUp, Users, Package, DollarSign, Globe, Crown } from 'lucide-react';

interface Props {
  accent: string;
}

export default function MarketingPlanSection({ accent }: Props) {
  const [openSection, setOpenSection] = useState<string | null>('packages');

  const toggle = (key: string) => setOpenSection(openSection === key ? null : key);

  const membershipPackages = [
    { name: 'Starter', price: '₱1,999 - ₱3,999', cv: '0 CV', sv: '1,000 SV', drb: '₱1,000', bmb: '0 Pair', color: '#64748B' },
    { name: 'Builder', price: '₱5,999', cv: '5,000 CV', sv: '5,000 SV', drb: '₱1,000', bmb: '500 Pair', color: '#3B82F6' },
    { name: 'Bronze', price: '₱15,999', cv: '15,000 CV', sv: '15,000 SV', drb: '₱3,000', bmb: '1,500 Pair', color: '#CD7F32' },
    { name: 'Silver', price: '₱30,999', cv: '30,000 CV', sv: '30,000 SV', drb: '₱6,000', bmb: '3,000 Pair', color: '#94A3B8' },
    { name: 'Gold', price: '₱159,999', cv: '150,000 CV', sv: '150,000 SV', drb: '₱30,000', bmb: '15,000 Pair', color: '#FACC15' },
  ];

  const bmbDailyLimits = [
    { pkg: 'Builder', maxPerCycle: '₱7,500', maxPairs: '15 Pairs' },
    { pkg: 'Bronze', maxPerCycle: '₱15,000', maxPairs: '30 Pairs' },
    { pkg: 'Silver', maxPerCycle: '₱20,000', maxPairs: '40 Pairs' },
    { pkg: 'Gold', maxPerCycle: '₱30,000', maxPairs: '60 Pairs' },
  ];

  const estimatedIncome = [
    { pkg: 'Builder', daily: '₱15,000', weekly: '₱105,000', monthly: '₱450,000', color: '#3B82F6' },
    { pkg: 'Bronze', daily: '₱30,000', weekly: '₱210,000', monthly: '₱900,000', color: '#CD7F32' },
    { pkg: 'Silver', daily: '₱40,000', weekly: '₱280,000', monthly: '₱1,200,000', color: '#94A3B8' },
    { pkg: 'Gold', daily: '₱60,000', weekly: '₱420,000', monthly: '₱1,800,000', color: '#FACC15' },
  ];

  const strongLeg = [
    { pkg: 'Builder', max: '75,000' },
    { pkg: 'Bronze', max: '150,000' },
    { pkg: 'Silver', max: '200,000' },
    { pkg: 'Gold', max: '300,000' },
  ];

  const renewalReqs = [
    { pkg: 'Builder', req: '50,000' },
    { pkg: 'Bronze', req: '150,000' },
    { pkg: 'Silver', req: '300,000' },
    { pkg: 'Gold', req: '750,000' },
  ];

  const usbLevels = [
    { level: 'Depot', pct: '4%' },
    { level: 'Stockist', pct: '4%' },
    { level: 'Level 1', pct: '8%' },
    { level: 'Levels 2–10', pct: '4%' },
    { level: 'Levels 11–20', pct: '2%' },
    { level: 'Levels 21–50', pct: '0.8%' },
    { level: 'Levels 51–100', pct: '0.4%' },
  ];

  const grsRanks = [
    {
      rank: 'Senior', shares: '1 Share', maturity: '₱500K', icon: '🥈',
      reqs: ['Gold Account'],
      color: '#94A3B8',
      reward: null,
      limit: null,
    },
    {
      rank: 'Supervisor', shares: '2 Shares', maturity: '₱1M', icon: '🥇',
      reqs: ['1 Gold Left', '1 Gold Right', '₱50K Personal Retail', '₱500K Direct Sales'],
      color: '#F59E0B',
      reward: 'Golden Gift Package',
      limit: null,
    },
    {
      rank: 'Manager', shares: '3 Shares', maturity: '₱2M', icon: '💼',
      reqs: ['2 Gold Left', '2 Gold Right', '₱100K Personal Retail', '₱1M Direct Sales'],
      color: '#10B981',
      reward: '🌏 Local Travel Incentive',
      limit: null,
    },
    {
      rank: 'Director', shares: '4 Shares', maturity: '₱3M', icon: '🏛️',
      reqs: ['4 Gold Left', '4 Gold Right', '₱200K Personal Retail', '₱2M Direct Sales'],
      color: '#6366F1',
      reward: '📱 Gadget Incentive',
      limit: null,
    },
    {
      rank: 'Executive', shares: '5 Shares', maturity: '₱4M', icon: '⭐',
      reqs: ['6 Gold Left', '6 Gold Right', '₱500K Personal Retail', '₱5M Direct Sales'],
      color: '#EC4899',
      reward: '✈️ Overseas Travel Program for Two',
      limit: null,
    },
    {
      rank: 'Royalty', shares: '5 Shares', maturity: '₱5M', icon: '👑',
      reqs: ['10 Gold Left', '10 Gold Right', '₱1M Personal Retail', '₱10M Direct Sales'],
      color: '#F97316',
      reward: '🚗 Car Program',
      limit: null,
    },
    {
      rank: 'Legendary', shares: '1 Lifetime Share', maturity: '—', icon: '🌟',
      reqs: ['1 Executive Left', '1 Executive Right', '₱5M Personal Retail', '₱50M Direct Sales'],
      color: '#FACC15',
      reward: '🏠 House and Lot Program',
      limit: 'First 25 Achievers Only',
    },
  ];

  const stockistDepot = [
    { pkg: 'Starter', stockist: '₱100', depot: '₱200' },
    { pkg: 'Builder', stockist: '₱150', depot: '₱300' },
    { pkg: 'Bronze', stockist: '₱250', depot: '₱500' },
    { pkg: 'Silver', stockist: '₱500', depot: '₱1,000' },
    { pkg: 'Gold', stockist: '₱2,500', depot: '₱5,000' },
  ];

  const retailAllocation = [
    { item: 'Platform Fee', amount: '₱100', rate: '10%' },
    { item: 'Payment & Handling', amount: '₱50', rate: '5%' },
    { item: 'PV Allocation', amount: '₱100', rate: '10% (2.5%–20%)' },
    { item: 'GRS Allocation', amount: '₱100', rate: '10% (2.5%–20%)' },
  ];

  const sections = [
    { key: 'packages', label: 'Membership Packages', icon: Package },
    { key: 'drb', label: 'Direct Referral Bonus', icon: Users },
    { key: 'bmb', label: 'Binary Match Bonus', icon: TrendingUp },
    { key: 'usb', label: 'Unilevel Sales Bonus', icon: Globe },
    { key: 'grs', label: 'Gold Ranking Shares', icon: Crown },
    { key: 'retail', label: 'Retail & Distribution', icon: DollarSign },
  ];

  return (
    <section className="py-16" style={{ background: 'rgba(255,255,255,0.72)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <span
            className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3"
            style={{ background: `${accent}15`, color: accent }}
          >
            MARKETING PLAN
          </span>
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#0F172A' }}>
            Compensation & Marketing Plan
          </h2>
          <p className="max-w-xl mx-auto text-sm" style={{ color: '#64748B' }}>
            Transparent, performance-based rewards designed to grow with your network and marketplace activity.
          </p>
        </div>

        {/* Section Nav Pills */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {sections.map((s) => (
            <button
              key={s.key}
              onClick={() => toggle(s.key)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
              style={{
                background: openSection === s.key ? accent : `${accent}12`,
                color: openSection === s.key ? '#fff' : accent,
                border: `1px solid ${accent}30`,
              }}
            >
              <s.icon size={12} />
              {s.label}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {/* ── MEMBERSHIP PACKAGES ── */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #E2E8F0' }}>
            <button
              onClick={() => toggle('packages')}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
              style={{ background: openSection === 'packages' ? `${accent}08` : '#fff' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${accent}15` }}>
                  <Package size={16} style={{ color: accent }} />
                </div>
                <span className="font-bold text-sm" style={{ color: '#0F172A' }}>Membership Packages</span>
              </div>
              {openSection === 'packages' ? <ChevronUp size={16} style={{ color: accent }} /> : <ChevronDown size={16} style={{ color: '#94A3B8' }} />}
            </button>
            {openSection === 'packages' && (
              <div className="px-5 pt-4 pb-5">
                <div className="overflow-x-auto -mx-1">
                  <table className="w-full text-xs min-w-[480px]">
                    <thead>
                      <tr style={{ background: '#F8FAFC' }}>
                        <th className="text-left px-3 py-2.5 font-bold rounded-tl-xl" style={{ color: '#475569' }}>Package</th>
                        <th className="text-right px-3 py-2.5 font-bold" style={{ color: '#475569' }}>Price</th>
                        <th className="text-right px-3 py-2.5 font-bold" style={{ color: '#475569' }}>CV</th>
                        <th className="text-right px-3 py-2.5 font-bold" style={{ color: '#475569' }}>SV</th>
                        <th className="text-right px-3 py-2.5 font-bold" style={{ color: '#475569' }}>Direct Referral</th>
                        <th className="text-right px-3 py-2.5 font-bold rounded-tr-xl" style={{ color: '#475569' }}>Binary Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {membershipPackages.map((pkg, i) => (
                        <tr key={pkg.name} style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: pkg.color }} />
                              <span className="font-bold" style={{ color: '#0F172A' }}>{pkg.name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right font-semibold" style={{ color: '#334155' }}>{pkg.price}</td>
                          <td className="px-3 py-3 text-right" style={{ color: '#64748B' }}>{pkg.cv}</td>
                          <td className="px-3 py-3 text-right" style={{ color: '#64748B' }}>{pkg.sv}</td>
                          <td className="px-3 py-3 text-right font-semibold" style={{ color: '#16A34A' }}>{pkg.drb}</td>
                          <td className="px-3 py-3 text-right font-semibold" style={{ color: accent }}>{pkg.bmb}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* ── DIRECT REFERRAL BONUS ── */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #E2E8F0' }}>
            <button
              onClick={() => toggle('drb')}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
              style={{ background: openSection === 'drb' ? `${accent}08` : '#fff' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${accent}15` }}>
                  <Users size={16} style={{ color: accent }} />
                </div>
                <span className="font-bold text-sm" style={{ color: '#0F172A' }}>Direct Referral Bonus (DRB)</span>
              </div>
              {openSection === 'drb' ? <ChevronUp size={16} style={{ color: accent }} /> : <ChevronDown size={16} style={{ color: '#94A3B8' }} />}
            </button>
            {openSection === 'drb' && (
              <div className="px-5 pt-4 pb-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <div className="rounded-xl p-4 mb-3" style={{ background: `${accent}08`, border: `1px solid ${accent}20` }}>
                      <p className="text-xs font-bold mb-1" style={{ color: accent }}>PURPOSE</p>
                      <p className="text-sm" style={{ color: '#334155' }}>Reward members for personally referring new members.</p>
                    </div>
                    <div className="rounded-xl p-4" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                      <p className="text-xs font-bold mb-1" style={{ color: '#16A34A' }}>REWARD</p>
                      <p className="text-sm font-bold" style={{ color: '#15803D' }}>20%–50% of Sponsorship Value (SV)</p>
                    </div>
                  </div>
                  {/* Flow diagram */}
                  <div className="flex flex-col items-center justify-center gap-0">
                    {['Referral', 'Registration', 'Package Purchase', 'Direct Referral Bonus'].map((step, i, arr) => (
                      <React.Fragment key={step}>
                        <div
                          className="w-full max-w-[200px] text-center px-4 py-2.5 rounded-xl text-xs font-bold"
                          style={{
                            background: i === arr.length - 1 ? accent : `${accent}12`,
                            color: i === arr.length - 1 ? '#fff' : accent,
                            border: `1px solid ${accent}30`,
                          }}
                        >
                          {step}
                        </div>
                        {i < arr.length - 1 && (
                          <div className="text-lg leading-none my-0.5" style={{ color: `${accent}60` }}>↓</div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── BINARY MATCH BONUS ── */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #E2E8F0' }}>
            <button
              onClick={() => toggle('bmb')}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
              style={{ background: openSection === 'bmb' ? `${accent}08` : '#fff' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${accent}15` }}>
                  <TrendingUp size={16} style={{ color: accent }} />
                </div>
                <span className="font-bold text-sm" style={{ color: '#0F172A' }}>Binary Match Bonus (BMB)</span>
              </div>
              {openSection === 'bmb' ? <ChevronUp size={16} style={{ color: accent }} /> : <ChevronDown size={16} style={{ color: '#94A3B8' }} />}
            </button>
            {openSection === 'bmb' && (
              <div className="px-5 pt-4 pb-5 space-y-5">
                {/* Rate summary */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Commission Rate', value: '10% of CV' },
                    { label: 'Max Cycles/Day', value: '2 Cycles' },
                    { label: 'Points Per Pair', value: '500 Points' },
                  ].map((item) => (
                    <div key={item.label} className="rounded-xl p-3 text-center" style={{ background: `${accent}08`, border: `1px solid ${accent}20` }}>
                      <p className="text-xs font-bold mb-1" style={{ color: accent }}>{item.label}</p>
                      <p className="text-sm font-bold" style={{ color: '#0F172A' }}>{item.value}</p>
                    </div>
                  ))}
                </div>

                {/* Daily Cycle Limits */}
                <div>
                  <p className="text-xs font-bold mb-2" style={{ color: '#475569' }}>DAILY CYCLE LIMITS</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[320px]">
                      <thead>
                        <tr style={{ background: '#F8FAFC' }}>
                          <th className="text-left px-3 py-2 font-bold rounded-tl-xl" style={{ color: '#475569' }}>Package</th>
                          <th className="text-right px-3 py-2 font-bold" style={{ color: '#475569' }}>Max Per Cycle</th>
                          <th className="text-right px-3 py-2 font-bold rounded-tr-xl" style={{ color: '#475569' }}>Maximum Pairs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bmbDailyLimits.map((row, i) => (
                          <tr key={row.pkg} style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                            <td className="px-3 py-2.5 font-semibold" style={{ color: '#334155' }}>{row.pkg}</td>
                            <td className="px-3 py-2.5 text-right font-semibold" style={{ color: '#16A34A' }}>{row.maxPerCycle}</td>
                            <td className="px-3 py-2.5 text-right" style={{ color: '#64748B' }}>{row.maxPairs}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Estimated Maximum Income */}
                <div>
                  <p className="text-xs font-bold mb-2" style={{ color: '#475569' }}>ESTIMATED MAXIMUM INCOME</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {estimatedIncome.map((row) => (
                      <div key={row.pkg} className="rounded-xl p-4" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-3 h-3 rounded-full" style={{ background: row.color }} />
                          <span className="font-bold text-sm" style={{ color: '#0F172A' }}>{row.pkg}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          {[
                            { label: 'Daily', value: row.daily },
                            { label: 'Weekly', value: row.weekly },
                            { label: 'Monthly', value: row.monthly },
                          ].map((item) => (
                            <div key={item.label}>
                              <p className="text-xs font-bold" style={{ color: '#16A34A' }}>{item.value}</p>
                              <p className="text-xs" style={{ color: '#94A3B8' }}>{item.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Strong Leg Retention */}
                <div>
                  <p className="text-xs font-bold mb-2" style={{ color: '#475569' }}>STRONG LEG RETENTION</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {strongLeg.map((row) => (
                      <div key={row.pkg} className="rounded-xl p-3 text-center" style={{ background: `${accent}08`, border: `1px solid ${accent}20` }}>
                        <p className="text-xs font-semibold mb-1" style={{ color: '#334155' }}>{row.pkg}</p>
                        <p className="text-sm font-bold" style={{ color: accent }}>{row.max}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fifth Pair Rewards */}
                <div className="rounded-xl p-4" style={{ background: 'linear-gradient(135deg, #FEF9C3, #FEF3C7)', border: '1px solid #FDE68A' }}>
                  <p className="text-xs font-bold mb-3" style={{ color: '#92400E' }}>🎁 FIFTH PAIR REWARDS</p>
                  <p className="text-xs mb-3" style={{ color: '#78350F' }}>Every 5th successful pair contributes to:</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="rounded-lg p-2 text-center" style={{ background: 'rgba(255,255,255,0.7)' }}>
                      <p className="text-sm font-bold" style={{ color: '#92400E' }}>50%</p>
                      <p className="text-xs" style={{ color: '#78350F' }}>Products</p>
                    </div>
                    <div className="rounded-lg p-2 text-center" style={{ background: 'rgba(255,255,255,0.7)' }}>
                      <p className="text-sm font-bold" style={{ color: '#92400E' }}>50%</p>
                      <p className="text-xs" style={{ color: '#78350F' }}>Rewards</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { icon: '✈️', label: 'Travel', img: '/assets/images/reward-travel.png', alt: 'Luxury tropical beach resort with infinity pool and palm trees at sunset' },
                      { icon: '📱', label: 'Gadgets', img: '/assets/images/reward-gadgets.png', alt: 'Premium tech gadgets including smartphone, earbuds, smartwatch and laptop' },
                      { icon: '🚗', label: 'Vehicles', img: '/assets/images/reward-vehicles.png', alt: 'Luxury sports car parked on scenic mountain road' },
                      { icon: '💎', label: 'Lifestyle', img: '/assets/images/reward-lifestyle.png', alt: 'Elegant spa and wellness lifestyle with champagne and premium products' },
                    ].map((r) => (
                      <div key={r.label} className="flex flex-col items-center gap-1.5 rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.9)' }}>
                        <div className="w-full h-40 overflow-hidden">
                          <img src={r.img} alt={r.alt} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex items-center gap-1 pb-2 px-2">
                          <span className="text-sm">{r.icon}</span>
                          <span className="text-xs font-semibold" style={{ color: '#92400E' }}>{r.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Renewal Requirements */}
                <div>
                  <p className="text-xs font-bold mb-2" style={{ color: '#475569' }}>RENEWAL REQUIREMENTS</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[280px]">
                      <thead>
                        <tr style={{ background: '#F8FAFC' }}>
                          <th className="text-left px-3 py-2 font-bold rounded-tl-xl" style={{ color: '#475569' }}>Package</th>
                          <th className="text-right px-3 py-2 font-bold rounded-tr-xl" style={{ color: '#475569' }}>Pairing Achieved</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renewalReqs.map((row, i) => (
                          <tr key={row.pkg} style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                            <td className="px-3 py-2.5 font-semibold" style={{ color: '#334155' }}>{row.pkg}</td>
                            <td className="px-3 py-2.5 text-right font-semibold" style={{ color: accent }}>{row.req}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── UNILEVEL SALES BONUS ── */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #E2E8F0' }}>
            <button
              onClick={() => toggle('usb')}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
              style={{ background: openSection === 'usb' ? `${accent}08` : '#fff' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${accent}15` }}>
                  <Globe size={16} style={{ color: accent }} />
                </div>
                <span className="font-bold text-sm" style={{ color: '#0F172A' }}>Unilevel Sales Bonus (USB)</span>
              </div>
              {openSection === 'usb' ? <ChevronUp size={16} style={{ color: accent }} /> : <ChevronDown size={16} style={{ color: '#94A3B8' }} />}
            </button>
            {openSection === 'usb' && (
              <div className="px-5 pt-4 pb-5">
                {/* Monthly Qualification Requirement */}
                <div className="rounded-xl p-4 mb-4" style={{ background: '#FFF7ED', border: '1px solid #FED7AA' }}>
                  <p className="text-xs font-bold mb-2" style={{ color: '#C2410C' }}>📋 MONTHLY QUALIFICATION REQUIREMENT</p>
                  <p className="text-xs mb-2" style={{ color: '#7C2D12' }}>To qualify for Unilevel Sales Bonus each month, you must meet <strong>at least one</strong> of the following:</p>
                  <ul className="space-y-1.5">
                    <li className="flex items-start gap-2 text-xs" style={{ color: '#7C2D12' }}>
                      <span className="mt-0.5 flex-shrink-0" style={{ color: '#EA580C' }}>✓</span>
                      <span>Have at least <strong>one product purchase</strong> from the previous calendar month</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs" style={{ color: '#7C2D12' }}>
                      <span className="mt-0.5 flex-shrink-0" style={{ color: '#EA580C' }}>✓</span>
                      <span>Be a <strong>new member</strong> who joined in the current or last calendar month</span>
                    </li>
                  </ul>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <div className="rounded-xl p-4 mb-4" style={{ background: `${accent}08`, border: `1px solid ${accent}20` }}>
                      <p className="text-xs font-bold mb-1" style={{ color: accent }}>HIGHLIGHTS</p>
                      <ul className="space-y-1">
                        {['100 Levels', 'Lifetime Reorder Income', 'Product-Based Sales'].map((h) => (
                          <li key={h} className="flex items-center gap-2 text-xs" style={{ color: '#334155' }}>
                            <span style={{ color: '#16A34A' }}>✓</span> {h}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr style={{ background: '#F8FAFC' }}>
                            <th className="text-left px-3 py-2 font-bold rounded-tl-xl" style={{ color: '#475569' }}>Level</th>
                            <th className="text-right px-3 py-2 font-bold rounded-tr-xl" style={{ color: '#475569' }}>%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usbLevels.map((row, i) => (
                            <tr key={row.level} style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                              <td className="px-3 py-2 font-medium" style={{ color: '#334155' }}>{row.level}</td>
                              <td className="px-3 py-2 text-right font-bold" style={{ color: '#16A34A' }}>{row.pct}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* 100-Level visual diagram */}
                  <div className="flex flex-col items-center">
                    <p className="text-xs font-bold mb-3" style={{ color: '#475569' }}>100-LEVEL DIAGRAM</p>
                    <div className="w-full space-y-0.5">
                      {[
                        { label: 'You', width: '100%', bg: accent, text: '#fff', bold: true },
                        { label: 'Level 1 — 8%', width: '92%', bg: `${accent}CC`, text: '#fff', bold: false },
                        { label: 'Levels 2–10 — 4%', width: '82%', bg: `${accent}AA`, text: '#fff', bold: false },
                        { label: 'Levels 11–20 — 2%', width: '72%', bg: `${accent}88`, text: '#fff', bold: false },
                        { label: 'Levels 21–50 — 0.8%', width: '60%', bg: `${accent}66`, text: '#fff', bold: false },
                        { label: 'Levels 51–100 — 0.4%', width: '48%', bg: `${accent}44`, text: '#334155', bold: false },
                      ].map((row, i) => (
                        <div
                          key={i}
                          className="mx-auto rounded-lg px-2 py-1.5 text-center text-xs font-medium"
                          style={{ width: row.width, background: row.bg, color: row.text, fontWeight: row.bold ? 700 : 500 }}
                        >
                          {row.label}
                        </div>
                      ))}
                      <div className="text-center mt-2">
                        <span className="text-xs font-bold" style={{ color: '#94A3B8' }}>↕ 100 Levels Total</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── GOLD RANKING SHARES ── */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #E2E8F0' }}>
            <button
              onClick={() => toggle('grs')}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
              style={{ background: openSection === 'grs' ? `${accent}08` : '#fff' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${accent}15` }}>
                  <Crown size={16} style={{ color: accent }} />
                </div>
                <span className="font-bold text-sm" style={{ color: '#0F172A' }}>Gold Ranking Shares (GRS)</span>
              </div>
              {openSection === 'grs' ? <ChevronUp size={16} style={{ color: accent }} /> : <ChevronDown size={16} style={{ color: '#94A3B8' }} />}
            </button>
            {openSection === 'grs' && (
              <div className="px-5 pt-4 pb-5">
                <div className="space-y-3">
                  {grsRanks.map((rank, i) => (
                    <div
                      key={rank.rank}
                      className="rounded-xl p-4 relative overflow-hidden"
                      style={{
                        background: i === grsRanks.length - 1
                          ? 'linear-gradient(135deg, #FEF9C3, #FEF3C7)'
                          : '#F8FAFC',
                        border: `1px solid ${rank.color}40`,
                      }}
                    >
                      {/* Rank badge */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                            style={{ background: `${rank.color}20`, border: `1px solid ${rank.color}40` }}
                          >
                            {rank.icon}
                          </div>
                          <div>
                            <p className="font-bold text-sm" style={{ color: '#0F172A' }}>{rank.rank}</p>
                            <p className="text-xs" style={{ color: '#64748B' }}>{rank.shares}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {rank.maturity !== '—' && (
                            <div
                              className="px-3 py-1 rounded-full text-xs font-bold"
                              style={{ background: `${rank.color}20`, color: rank.color }}
                            >
                              {rank.maturity} Maturity
                            </div>
                          )}
                          {rank.limit && (
                            <div
                              className="px-2 py-0.5 rounded-full text-xs font-bold"
                              style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}
                            >
                              🏆 {rank.limit}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Requirements */}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {rank.reqs.map((req) => (
                          <span
                            key={req}
                            className="px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ background: '#fff', color: '#475569', border: '1px solid #E2E8F0' }}
                          >
                            {req}
                          </span>
                        ))}
                      </div>
                      {/* Reward badge */}
                      {rank.reward && (
                        <div
                          className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
                          style={{ background: `${rank.color}18`, color: rank.color, border: `1px solid ${rank.color}40` }}
                        >
                          🎁 {rank.reward}
                        </div>
                      )}
                      {/* Rank ladder connector */}
                      {i < grsRanks.length - 1 && (
                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-sm" style={{ color: `${rank.color}80` }}>↓</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── RETAIL & DISTRIBUTION ── */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #E2E8F0' }}>
            <button
              onClick={() => toggle('retail')}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
              style={{ background: openSection === 'retail' ? `${accent}08` : '#fff' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${accent}15` }}>
                  <DollarSign size={16} style={{ color: accent }} />
                </div>
                <span className="font-bold text-sm" style={{ color: '#0F172A' }}>Retail & Distribution</span>
              </div>
              {openSection === 'retail' ? <ChevronUp size={16} style={{ color: accent }} /> : <ChevronDown size={16} style={{ color: '#94A3B8' }} />}
            </button>
            {openSection === 'retail' && (
              <div className="px-5 pt-4 pb-5 space-y-5">
                {/* Retail Discount Illustration */}
                <div>
                  <p className="text-xs font-bold mb-3" style={{ color: '#475569' }}>RETAIL DISCOUNT ILLUSTRATION</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col items-center gap-1">
                      {[
                        { label: 'Retail Price', value: '₱1,429', bg: '#F8FAFC', border: '#E2E8F0', textColor: '#334155' },
                        { label: 'Member Price', value: '₱1,000', bg: '#F0FDF4', border: '#BBF7D0', textColor: '#16A34A' },
                      ].map((item, i, arr) => (
                        <React.Fragment key={item.label}>
                          <div
                            className="w-full rounded-xl px-4 py-3 text-center"
                            style={{ background: item.bg, border: `1px solid ${item.border}` }}
                          >
                            <p className="text-xs font-bold mb-0.5" style={{ color: '#94A3B8' }}>{item.label}</p>
                            <p className="text-lg font-bold" style={{ color: item.textColor }}>{item.value}</p>
                          </div>
                          {i < arr.length - 1 && <div className="text-sm" style={{ color: '#94A3B8' }}>↓</div>}
                        </React.Fragment>
                      ))}
                      <div className="w-full rounded-xl px-4 py-3 text-center mt-1" style={{ background: `${accent}08`, border: `1px solid ${accent}30` }}>
                        <p className="text-xs font-bold mb-0.5" style={{ color: accent }}>Estimated Gross Income</p>
                        <p className="text-lg font-bold" style={{ color: accent }}>₱650–₱850</p>
                        <p className="text-xs" style={{ color: '#64748B' }}>45%–80%</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-bold mb-2" style={{ color: '#475569' }}>ALLOCATION</p>
                      <table className="w-full text-xs">
                        <thead>
                          <tr style={{ background: '#F8FAFC' }}>
                            <th className="text-left px-3 py-2 font-bold rounded-tl-xl" style={{ color: '#475569' }}>Item</th>
                            <th className="text-right px-3 py-2 font-bold" style={{ color: '#475569' }}>Amount</th>
                            <th className="text-right px-3 py-2 font-bold rounded-tr-xl" style={{ color: '#475569' }}>Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {retailAllocation.map((row, i) => (
                            <tr key={row.item} style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                              <td className="px-3 py-2" style={{ color: '#334155' }}>{row.item}</td>
                              <td className="px-3 py-2 text-right font-semibold" style={{ color: '#334155' }}>{row.amount}</td>
                              <td className="px-3 py-2 text-right" style={{ color: '#64748B' }}>{row.rate}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Stockist & Depot Income */}
                <div>
                  <p className="text-xs font-bold mb-2" style={{ color: '#475569' }}>STOCKIST & DEPOT INCOME</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[280px]">
                      <thead>
                        <tr style={{ background: '#F8FAFC' }}>
                          <th className="text-left px-3 py-2 font-bold rounded-tl-xl" style={{ color: '#475569' }}>Package</th>
                          <th className="text-right px-3 py-2 font-bold" style={{ color: '#475569' }}>Stockist</th>
                          <th className="text-right px-3 py-2 font-bold rounded-tr-xl" style={{ color: '#475569' }}>Depot</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockistDepot.map((row, i) => (
                          <tr key={row.pkg} style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                            <td className="px-3 py-2.5 font-semibold" style={{ color: '#334155' }}>{row.pkg}</td>
                            <td className="px-3 py-2.5 text-right font-semibold" style={{ color: '#16A34A' }}>{row.stockist}</td>
                            <td className="px-3 py-2.5 text-right font-semibold" style={{ color: accent }}>{row.depot}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Lifetime Reorder Benefits */}
                <div className="rounded-xl p-4" style={{ background: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)', border: '1px solid #BFDBFE' }}>
                  <p className="text-xs font-bold mb-3" style={{ color: '#1D4ED8' }}>♾️ LIFETIME REORDER BENEFITS</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Stockist Royalty', value: '4% of PV' },
                      { label: 'Depot Royalty', value: '4% of PV' },
                      { label: 'Lifetime Discount (Stockist)', value: '5%' },
                      { label: 'Lifetime Discount (Depot)', value: '10%' },
                    ].map((item) => (
                      <div key={item.label} className="rounded-lg p-3 text-center" style={{ background: 'rgba(255,255,255,0.8)' }}>
                        <p className="text-sm font-bold mb-0.5" style={{ color: '#1D4ED8' }}>{item.value}</p>
                        <p className="text-xs" style={{ color: '#3B82F6' }}>{item.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-xs mt-6" style={{ color: '#94A3B8' }}>
          * Income figures are estimates based on maximum performance. Actual earnings depend on individual effort, network activity, and market conditions.
        </p>
      </div>
    </section>
  );
}
